import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import Home from "@/pages/Home";
import ApplyPage from "@/pages/ApplyPage";
import CpsApplyPage from "@/pages/CpsApplyPage";
import SelfFundedRegisterPage from "@/pages/SelfFundedRegisterPage";
import CpsConfirmationPage from "@/pages/CpsConfirmationPage";
import ContactPage from "@/pages/ContactPage";
import ProgramPage from "@/pages/ProgramPage";
import MembershipPage from "@/pages/MembershipPage";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import { bootPixels, captureAttribution, metaPageViewOnRouteChange } from "@/lib/analytics";

/** Fires a Meta Pixel PageView on every SPA route change (the base pixel in
 *  index.html only fires on the initial hard load). Placed inside the
 *  BrowserRouter so useLocation is available. */
function RouteChangePixel() {
    const location = useLocation();
    useEffect(() => {
        metaPageViewOnRouteChange(location.pathname);
    }, [location.pathname]);
    return null;
}

function useLenisSmooth() {
    useEffect(() => {
        const lenis = new Lenis({
            duration: 1.15,
            easing: (t) => 1 - Math.pow(1 - t, 3),
            smoothWheel: true,
            lerp: 0.09,
        });
        function raf(time) {
            lenis.raf(time);
            requestAnimationFrame(raf);
        }
        const r = requestAnimationFrame(raf);
        return () => {
            cancelAnimationFrame(r);
            lenis.destroy();
        };
    }, []);
}

function SiteRoutes() {
    useLenisSmooth();
    return (
        <>
            <div className="grain-overlay" aria-hidden />
            <Layout>
                <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/apply" element={<ApplyPage />} />
                    <Route path="/apply/cps-2026" element={<CpsApplyPage />} />
                    <Route path="/apply/cps-2026/self-funded" element={<SelfFundedRegisterPage />} />
                    <Route path="/apply/cps-2026/received" element={<CpsConfirmationPage />} />
                    <Route path="/apply/:slug" element={<ApplyPage />} />
                    <Route path="/contact" element={<ContactPage />} />
                    <Route path="/programs/:slug" element={<ProgramPage />} />
                    <Route path="/membership" element={<MembershipPage />} />
                </Routes>
            </Layout>
        </>
    );
}

function App() {
    // Capture UTMs immediately on first paint, then boot pixels once analytics
    // config is fetched from the backend.
    useEffect(() => {
        captureAttribution();
        bootPixels();
    }, []);

    return (
        <BrowserRouter>
            <AuthProvider>
                <RouteChangePixel />
                <Routes>
                    <Route path="/admin/login" element={<AdminLogin />} />
                    <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                    <Route path="/*" element={<SiteRoutes />} />
                </Routes>
                <Toaster
                    theme="dark"
                    position="bottom-right"
                    toastOptions={{
                        style: {
                            background: "#061124",
                            color: "#FFFFF0",
                            border: "1px solid rgba(201,162,75,0.5)",
                            borderRadius: 0,
                            fontFamily: "'Space Mono', monospace",
                            letterSpacing: "0.08em",
                            fontSize: 12,
                        },
                    }}
                />
            </AuthProvider>
        </BrowserRouter>
    );
}

export default App;
